import { AddressLink, PtoV3PoolQuery, PtoV3PoolResult } from 'Modules'
import type { Address } from 'viem'
import { parseEventLogs } from 'viem'

import abiPTODistributionV3 from 'abi/abi-pto-distribution-v3.json'
import { AppAdmin } from 'api/app-admin'
import { TokenizeStatusEnum } from 'constants/pto'
import { isSameAddress, toPoolDistributionParams } from 'extends/pto-v3.extend'
import { writeContractAndWait } from 'hooks/contract/use-contract-viem'
import { ORCHESTRATOR_NOT_CONFIGURED, usePtoOrchestrator } from 'hooks/pto-v3/use-pto-orchestrator'
import { logError, logInfo } from 'utils/logger'

/**
 * PTO v3 step 5 + 6: open the sale through PTOOrchestrator.createDistributionPool
 * (vault + vault ONCHAINID + claim + pool in one tx) with the params the BE returns.
 *
 * - The query carries the raw price (USDC) / fee (%); the response is already in
 *   contract units and is passed through untouched.
 * - The claim signature is pre-signed by the BE over the predicted vault ONCHAINID
 *   address; the simulation result is compared with `predictedVaultIdentity` and the
 *   tx is NOT sent when they differ.
 * - The BE saves poolId / vaultAddress / vaultIdentity itself from the events; the
 *   PATCH /:id/contract call is only a fallback and never fails the flow.
 */
export const useCreatePoolDistributionV3 = () => {
  const { orchestratorAddress, contract, isAgent } = usePtoOrchestrator()

  const createPoolDistribution = async (
    account: Address,
    id: string,
    query?: PtoV3PoolQuery
  ): Promise<PtoV3PoolResult> => {
    if (!orchestratorAddress || !contract) {
      throw new Error(ORCHESTRATOR_NOT_CONFIGURED)
    }

    const res = await AppAdmin.ptoV3GetPoolDistributionArgs(id, query)
    const args = res.data?.data
    if (!args?.ptoAddress || !args?.salt || !args?.claimSignature) {
      throw new Error('The API did not return the create-pool-distribution params')
    }
    const params = toPoolDistributionParams(args)
    logInfo('PTO v3 createDistributionPool params:', params)

    // PTOOrchestrator upgrade (2026-10-06): createPoolDistribution was renamed createDistributionPool
    const { request, result } = await contract.simulate.createDistributionPool([params], {
      account,
    })
    const [vaultAddress, vaultIdentity] = result as [Address, Address]

    if (args.predictedVaultIdentity && !isSameAddress(args.predictedVaultIdentity, vaultIdentity)) {
      throw new Error(
        `Vault identity mismatch: the API predicted ${args.predictedVaultIdentity} but the simulation returned ${vaultIdentity}. Nothing was sent.`
      )
    }

    const { hash, receipt } = await writeContractAndWait(request)

    // PoolCreated is emitted by PTODistribution inside createDistributionPool
    const logs: any[] = parseEventLogs({
      abi: abiPTODistributionV3 as any,
      logs: receipt.logs,
      eventName: 'PoolCreated',
    })
    const poolId = logs.length ? Number(logs[0].args.poolId) : undefined

    return { vaultAddress, vaultIdentity, poolId, txHash: hash }
  }

  /**
   * Step 6 fallback - PATCH /admin/pto/:id/contract with vaultAddress + vaultIdentity (+ poolId).
   * The BE hook already stores these from PoolCreated / PoolDistributionCreated, so a
   * failure here is logged, not thrown. status and contractAddress are required by the endpoint.
   */
  const reportPoolCreated = async (
    id: string,
    result: PtoV3PoolResult,
    contractAddress: AddressLink | null | undefined,
    status: TokenizeStatusEnum = TokenizeStatusEnum.ACTIVE
  ) => {
    if (!contractAddress) {
      throw new Error('The PTO contract address is missing, cannot link the pool')
    }
    try {
      await AppAdmin.ptoV3UpdateContract(id, {
        status,
        contractAddress,
        vaultAddress: result.vaultAddress,
        vaultIdentity: result.vaultIdentity,
        ...(result.poolId !== undefined ? { poolId: result.poolId } : {}),
      })
      return true
    } catch (error) {
      logError(
        'Fallback link of the pool / vault on the BE failed (the hook will store it):',
        error
      )
      return false
    }
  }

  return { isAgent, orchestratorAddress, createPoolDistribution, reportPoolCreated }
}
